import { Rows2, Rows3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDensity } from './DensityProvider';
import type { Density } from './density';

const OPTIONS = [
  { density: 'comfortable', labelKey: 'density.comfortable', Icon: Rows2 },
  { density: 'compact', labelKey: 'density.compact', Icon: Rows3 },
] as const satisfies ReadonlyArray<{ density: Density; labelKey: string; Icon: typeof Rows2 }>;

/**
 * Segment sizes: 30px inside the header's 36px control (Brand Kit § CTA And Buttons, 36px), 44px
 * where the control is touched rather than clicked — the drawer's preferences.
 */
const SEGMENT_SIZE = { default: 'size-[30px]', touch: 'size-11' } as const;

/**
 * Density control, deliberately built as `ThemeToggle`'s twin — same track, same segment geometry,
 * same `aria-pressed` marking, same icon-only footprint with the name on `title`/`aria-label`.
 *
 * It sits beside theme and language in the header because all three are DISPLAY preferences and a
 * user looking for one looks for the others in the same place. There is room for it there only
 * because decision 5 emptied the header of the session group; on the old wrapping header a third
 * control would have been the one that pushed it onto a second row.
 *
 * Two rows of the glyph mean taller rows, three mean more of them — the icon says which way the
 * table goes, which a "density" label never manages to.
 */
export function DensityToggle({ size = 'default' }: { size?: keyof typeof SEGMENT_SIZE } = {}) {
  const { density, setDensity } = useDensity();
  const { t } = useTranslation('shell');

  return (
    <div
      className="bg-muted inline-flex items-center gap-0.5 rounded-md p-[3px]"
      role="group"
      aria-label={t('density.groupLabel')}
    >
      {OPTIONS.map(({ density: optionDensity, labelKey, Icon }) => {
        const label = t(labelKey);
        const active = density === optionDensity;
        return (
          <button
            key={optionDensity}
            type="button"
            aria-pressed={active}
            aria-label={label}
            title={label}
            onClick={() => setDensity(optionDensity)}
            className={`focus-visible:ring-ring inline-flex items-center justify-center rounded-sm
              border transition-colors focus-visible:ring-2 focus-visible:outline-none
              [&_svg]:size-4 ${SEGMENT_SIZE[size]} ${
                active
                  ? 'border-chip-border bg-card text-foreground shadow-[var(--shadow-1)]'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              }`}
          >
            <Icon aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
